import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { userService } from '../services/supabase';
import { FiUser, FiEdit2, FiTrash2, FiPlus, FiCheck, FiX, FiSearch } from 'react-icons/fi';

type User = {
  id: string;
  nome: string;
  email: string;
  avatar_url: string | null;
  tipo_usuario: 'admin' | 'comum';
};

const UserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await userService.getUsers();
      
      if (error) {
        throw new Error(error.message);
      }
      
      setUsers(data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar usuários');
      console.error('Erro ao buscar usuários:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    try {
      setLoading(true);
      const { error } = await userService.deleteUser(userToDelete.id);
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Atualiza a lista de usuários após a exclusão
      setUsers(users.filter(user => user.id !== userToDelete.id));
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir usuário');
      console.error('Erro ao excluir usuário:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  // Filtra usuários por termo de busca
  const filteredUsers = users.filter(user => 
    user.nome.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white font-raleway mb-2">
          Gerenciamento de <span className="text-vermelho-claro">Usuários</span>
        </h1>
        <p className="text-gray-300">
          Visualize, adicione, edite e exclua usuários da plataforma.
        </p>
      </div>

      {/* Barra de ações */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="relative w-full md:w-auto md:min-w-[300px]">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Buscar usuários..."
            className="input pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <Link 
          to="/usuarios/novo" 
          className="btn btn-primary py-2 px-4 flex items-center w-full md:w-auto justify-center"
        >
          <FiPlus className="mr-2" />
          Novo Usuário
        </Link>
      </div>

      {/* Lista de usuários */}
      {loading && users.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-vermelho-claro"></div>
        </div>
      ) : error ? (
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 text-center">
          <p className="text-red-200">{error}</p>
          <button 
            onClick={fetchUsers}
            className="mt-2 text-vermelho-claro hover:underline"
          >
            Tentar novamente
          </button>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 bg-gray-900/50 rounded-lg">
          <div className="w-16 h-16 bg-gray-800 rounded-full mx-auto flex items-center justify-center mb-4">
            <FiUser className="text-gray-600" size={24} />
          </div>
          <h3 className="text-xl font-medium text-gray-400 mb-2">
            Nenhum usuário encontrado
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            {users.length === 0 
              ? 'Não há usuários cadastrados. Clique em "Novo Usuário" para adicionar.'
              : 'Nenhum usuário corresponde aos termos de busca.'}
          </p>
        </div>
      ) : (
        <div className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-800">
              <thead className="bg-gray-800/50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-800/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full overflow-hidden bg-vermelho-escuro flex items-center justify-center">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt={user.nome} className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-white text-sm font-bold">
                              {user.nome.substring(0, 2).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">
                            {user.nome}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.tipo_usuario === 'admin' 
                          ? 'bg-vermelho-escuro text-white' 
                          : 'bg-blue-900 text-blue-200'
                      }`}>
                        {user.tipo_usuario === 'admin' ? 'Administrador' : 'Usuário Comum'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/usuarios/${user.id}`}
                        className="text-indigo-400 hover:text-indigo-300 mr-4"
                      >
                        <FiEdit2 className="inline" /> Editar
                      </Link>
                      <button
                        onClick={() => handleDeleteClick(user)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <FiTrash2 className="inline" /> Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={handleDeleteCancel}></div>
          <div className="relative bg-gray-900 rounded-lg max-w-md w-full mx-auto z-10 overflow-hidden animate-modal-appear">
            <div className="p-6">
              <div className="mb-4 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-red-900/50 flex items-center justify-center mb-4">
                  <FiTrash2 className="text-red-500" size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Excluir Usuário</h3>
                <p className="text-gray-300">
                  Tem certeza que deseja excluir o usuário <span className="font-bold text-white">{userToDelete.nome}</span>?
                </p>
                <p className="mt-2 text-sm text-red-400">
                  Esta ação não pode ser desfeita e removerá todos os dados e permissões associados.
                </p>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleDeleteCancel}
                  className="flex-1 py-2 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-md transition-colors font-medium flex items-center justify-center"
                >
                  <FiX className="mr-2" /> Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors font-medium flex items-center justify-center"
                >
                  <FiCheck className="mr-2" /> Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default UserList; 